import { MagazineHead } from "./_head";
import { Link } from "@inertiajs/react";
import { hairbookGlobalImageUrl } from "@/lib/_url";
import { useVideoAutoplay } from "@/lib/_use-video-autoplay";
import type { MagazineArticleCard, MagazineIndexProps, MagazineTagItem } from "./_props";

const accent = "#a4825a";
const accentDark = "#856843";
const softPink = "#b5557a";

const FEATURED_AREAS = [
  { name: "東京都", slug: "tokyo" },
  { name: "神奈川県", slug: "kanagawa" },
  { name: "愛知県", slug: "aichi" },
  { name: "京都府", slug: "kyoto" },
  { name: "大阪府", slug: "osaka" },
  { name: "福岡県", slug: "fukuoka" },
] as const;

export default function MagazineIndex({
  articles = [],
  tags = [],
  activeTag = "",
  query = "",
  offset = 0,
  total = 0,
}: MagazineIndexProps) {
  const featured = articles[0];
  const rankedArticles = articles.slice(0, 8);
  const recommendedArticles = activeTag
    ? articles
    : articles.filter((article) => article.tags.length > 0).slice(1, 9);
  const latestArticles = articles;
  const featureTags = tags.filter(({tag}) => ["髪質改善・縮毛矯正", "カラー", "カット"].includes(tag));

  return (
    <>
      <MagazineHead />
      <div className="min-h-screen bg-[#eef0f1] text-[#2b2b2b] pb-16 antialiased">
        <Header />

        {featured && <Hero article={featured} />}

        <main className="mx-auto max-w-[1120px]">
          <form action="/" method="get" className="px-4 pt-5 sm:px-6 lg:px-8">
            <label htmlFor="article-search" className="block text-sm font-bold">地域・悩み・サロン名から記事を探す</label>
            {activeTag && <input type="hidden" name="tag" value={activeTag} />}
            <div className="mt-2 flex gap-2">
              <input id="article-search" name="q" defaultValue={query} placeholder="例：表参道、白髪ぼかし、実習" maxLength={80} className="min-w-0 flex-1 rounded-lg border bg-white px-3 py-2" />
              <button className="rounded-lg bg-[#856843] px-4 py-2 text-white">検索</button>
            </div>
          </form>
          <TagScroller tags={tags} activeTag={activeTag} query={query} />

          <ArticleRail title="最近公開された記事" tone="gold" articles={rankedArticles} />
          <ArticleRail title="テーマから読む" tone="pink" articles={recommendedArticles} />

          <section className="px-4 py-6 sm:px-6 lg:px-8">
            <SectionHeader title="地域名で記事を探す" tone="gold" />
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {FEATURED_AREAS.map((area) => (
                <a
                  key={area.slug}
                  href={`/?q=${encodeURIComponent(area.name.replace(/[都府県]$/, ""))}&tag=${encodeURIComponent(activeTag)}`}
                  className="shrink-0 rounded-full border border-[#e7e7e7] bg-white px-4 py-2 text-[12px] font-medium text-[#5a5145] hover:border-[#d6c8b5]"
                >
                  {area.name}
                </a>
              ))}
              <a
                href="/"
                className="shrink-0 rounded-full border border-[#a4825a] bg-[#a4825a] px-4 py-2 text-[12px] font-medium text-white hover:bg-[#856843]"
              >
                すべての記事
              </a>
            </div>
          </section>

          {featureTags.length > 0 && (
            <section className="px-4 py-6 sm:px-6 lg:px-8">
              <SectionHeader title="お悩み・テーマ特集" tone="gold" />
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {featureTags.map((tag, index) => (
                  <FeatureCard key={tag.tag} tag={tag} article={articles.find(article => article.tags.includes(tag.tag))} index={index} />
                ))}
              </div>
            </section>
          )}

          <section className="px-4 py-6 sm:px-6 lg:px-8">
            <SectionHeader title={activeTag ? `${activeTag} の記事` : "新着記事"} tone="pink" />
            {latestArticles.length > 0 ? (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <EmptyState activeTag={activeTag} />
            )}
          </section>
          <nav aria-label="記事一覧のページ" className="flex items-center justify-center gap-5 px-4 py-6">
            {offset > 0 && <Link href={`/?q=${encodeURIComponent(query)}&tag=${encodeURIComponent(activeTag)}&offset=${Math.max(0, offset-24)}`}>前のページ</Link>}
            <span className="text-sm">{total}件中 {total ? offset+1 : 0}〜{Math.min(offset+articles.length,total)}件</span>
            {offset+24 < total && <Link href={`/?q=${encodeURIComponent(query)}&tag=${encodeURIComponent(activeTag)}&offset=${offset+24}`}>次のページ</Link>}
          </nav>
        </main>
      </div>
    </>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-30 border-b border-[#e7e7e7] bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1120px] items-center justify-center px-4 py-3 sm:px-6 lg:px-8">
        <Link href="/" className="text-center leading-none">
          <b className="font-serif text-xl font-semibold tracking-wide text-[#222]">Hairbook</b>
          <small className="mt-1 block text-[9px] tracking-[0.45em] text-[#8d8d8d]">MAGAZINE</small>
        </Link>
      </div>
    </header>
  );
}

function Hero({ article }: { article: MagazineArticleCard }) {
  return (
    <Link href={`/articles/${article.id}/`} className="group relative block h-[360px] max-h-[70svh] overflow-hidden bg-[#cdbfa9] md:h-[420px]">
      <ArticleImage article={article} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 via-black/25 to-transparent px-4 pb-6 sm:px-6 md:px-[calc((100vw-1120px)/2+32px)]">
        <span className="text-[10px] font-bold tracking-[0.18em] text-[#f0d9b8]">FEATURED</span>
        <h1 className="mt-2 max-w-[760px] text-[22px] font-bold leading-[1.45] text-white md:text-3xl md:leading-[1.45]">
          {article.title}
        </h1>
      </div>
    </Link>
  );
}

function TagScroller({ tags, activeTag, query }: { tags: MagazineTagItem[]; activeTag: string; query: string }) {
  return (
    <nav className="flex gap-2 overflow-x-auto px-4 py-5 sm:px-6 lg:px-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <Link
        href="/"
        className={`shrink-0 rounded-full border px-4 py-2 text-[12px] font-medium ${
          activeTag ? "border-[#e7e7e7] bg-white text-[#5a5145]" : "border-[#a4825a] bg-[#a4825a] text-white"
        }`}
      >
        すべて
      </Link>
      {tags.slice(0, 20).map((tag) => (
        <TagPill key={tag.tag} tag={tag} activeTag={activeTag} query={query} />
      ))}
    </nav>
  );
}

function TagPill({ tag, activeTag, query }: { tag: MagazineTagItem; activeTag: string; query: string }) {
  const active = activeTag === tag.tag;

  return (
    <Link
      href={`/?tag=${encodeURIComponent(tag.tag)}&q=${encodeURIComponent(query)}`}
      className={`shrink-0 rounded-full border px-4 py-2 text-[12px] font-medium ${
        active
          ? "border-[#a4825a] bg-[#a4825a] text-white"
          : "border-[#e7e7e7] bg-white text-[#5a5145] hover:border-[#d6c8b5]"
      }`}
    >
      {tag.tag}
      <span className="ml-1 opacity-60">{tag.count}</span>
    </Link>
  );
}

function ArticleRail({
  title,
  tone,
  articles,
  ranked = false,
}: {
  title: string;
  tone: "gold" | "pink";
  articles: MagazineArticleCard[];
  ranked?: boolean;
}) {
  if (articles.length === 0) return null;

  return (
    <section className="py-4">
      <div className="px-4 sm:px-6 lg:px-8">
        <SectionHeader title={title} tone={tone} />
      </div>
      <div className="mt-3 flex snap-x gap-3 overflow-x-auto px-4 pb-3 sm:px-6 lg:px-8">
        {articles.map((article, index) => (
          <ArticleCard key={article.id} article={article} rank={ranked ? index + 1 : undefined} rail />
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ title, tone }: { title: string; tone: "gold" | "pink" }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <h2 className="flex items-center gap-2 text-[17px] font-bold">
        <span style={{ color: tone === "gold" ? accent : softPink }}>◆</span>
        {title}
      </h2>
    </div>
  );
}

function FeatureCard({
  tag,
  article,
  index,
}: {
  tag: MagazineTagItem;
  article?: MagazineArticleCard;
  index: number;
}) {
  return (
    <Link
      href={`/?tag=${encodeURIComponent(tag.tag)}`}
      className="group relative flex h-[112px] items-center overflow-hidden rounded-[14px] bg-[#856843] text-white"
    >
      {article && <ArticleImage article={article} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#281c12]/80 to-[#281c12]/25" />
      <div className="relative px-4">
        <div className="text-[10px] tracking-widest opacity-85">SPECIAL {String(index + 1).padStart(2, "0")}</div>
        <h3 className="mt-1 text-[15px] font-bold">{tag.tag}</h3>
        <p className="mt-1 text-[11px] opacity-85">記事 {tag.count}本</p>
      </div>
    </Link>
  );
}

function ArticleCard({
  article,
  rank,
  rail = false,
}: {
  article: MagazineArticleCard;
  rank?: number;
  rail?: boolean;
}) {
  const primaryTag = article.tags[0];

  return (
    <Link
      href={`/articles/${article.id}/`}
      className={`group relative snap-start overflow-hidden rounded-[14px] border border-[#e7e7e7] bg-white transition hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(0,0,0,0.10)] ${
        rail ? "w-[min(76vw,220px)] shrink-0" : "grid grid-cols-[112px_1fr] sm:block"
      }`}
    >
      <div className={`relative bg-[#e7ddcd] ${rail ? "aspect-[4/3]" : "aspect-square sm:aspect-[4/3]"}`}>
        <ArticleImage article={article} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
        {rank && (
          <div
            className="absolute left-0 top-0 grid size-[34px] place-items-center rounded-br-xl font-serif text-base font-bold text-white"
            style={{ backgroundColor: rank === 1 ? "#c8a24a" : rank === 2 ? "#a9a9a9" : rank === 3 ? "#b07b4e" : accent }}
          >
            {rank}
          </div>
        )}
      </div>
      <div className="px-3 py-3">
        <h3 className="line-clamp-3 text-[13px] font-semibold leading-[1.5] sm:text-[12.5px] sm:leading-[1.45]">{article.title}</h3>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[#8d8d8d]">
          {primaryTag && <span style={{ color: accentDark }}>◍ {primaryTag}</span>}
          {article.publishedAt && <span>{formatShortDate(article.publishedAt)}</span>}
        </div>
      </div>
    </Link>
  );
}

function ArticleImage({ article, className }: { article: MagazineArticleCard; className: string }) {
  const videoRef = useVideoAutoplay<HTMLVideoElement>();
  const videoUrl = article.globalImageId && article.globalImageCategory === "video"
    ? hairbookGlobalImageUrl(article.globalImageId)
    : null;

  if (videoUrl) {
    return (
      <video
        ref={videoRef}
        src={videoUrl}
        className={className}
        muted
        loop
        playsInline
        preload="metadata"
      />
    );
  }

  const imageUrl = articleImageUrl(article);

  if (imageUrl) {
    return <img src={imageUrl} alt="" className={className} loading="lazy" referrerPolicy="no-referrer" />;
  }

  return (
    <div className={`${className} grid place-items-center bg-gradient-to-br from-[#e9ddcc] to-[#d8c3ad] px-3 text-center text-[11px] font-semibold text-white`}>
      {article.title}
    </div>
  );
}

function articleImageUrl(article: MagazineArticleCard): string | null {
  const globalImageUrl = article.globalImageId && article.globalImageCategory !== "video"
    ? hairbookGlobalImageUrl(article.globalImageId)
    : null;

  return globalImageUrl || article.eyecatchUrl;
}

function EmptyState({ activeTag }: { activeTag: string }) {
  return (
    <div className="py-20 text-center">
      <p className="text-sm text-black/40">
        {activeTag ? `"${activeTag}" の記事はまだありません` : "記事がありません"}
      </p>
    </div>
  );
}

function formatShortDate(value: string) {
  return new Date(value).toLocaleDateString("ja-JP", {
    month: "numeric",
    day: "numeric",
  });
}
